import React from 'react'
import { CaptionXS } from '@/atoms/Text'
import { useLocale, Locale } from '@/utils/LocaleUtil'
import { TranslateFunction, useTranslate } from '@/utils/translate/translate-client'

interface Props {
  earlyAccessDate?: Date
  isGuildMember: boolean
  publiclyAvailableDate?: Date
}

export const GuildAccessPill: React.FC<Props> = ({ isGuildMember, earlyAccessDate, publiclyAvailableDate }) => {
  const { t } = useTranslate('watch')
  const { locale } = useLocale()

  const text = getPillText({
    isGuildMember: isGuildMember,
    guildEarlyAccessDate: earlyAccessDate ? new Date(earlyAccessDate) : null,
    publicAccessDate: publiclyAvailableDate ? new Date(publiclyAvailableDate) : null,
    t: t,
    locale,
  })

  return (
    <div className="absolute bottom-3 right-2 z-10 p-1">
      {text && (
        <CaptionXS className="whitespace-nowrap rounded-full bg-core-gray-900/90 px-3 py-1 text-right uppercase backdrop-blur-lg">
          {text}
        </CaptionXS>
      )}
    </div>
  )
}

type GetPillTextParams = {
  isGuildMember: boolean
  guildEarlyAccessDate: Date | null
  t: TranslateFunction
  locale: Locale
  publicAccessDate: Date | null
}

function areDatesEqual(date1?: Date, date2?: Date) {
  if (!date1 || !date2) return false
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  )
}

function getPillText({ isGuildMember, guildEarlyAccessDate, t, locale, publicAccessDate }: GetPillTextParams) {
  const now = Date.now()
  const fortnightAgo = new Date(Date.now() - 12096e5)

  if (!guildEarlyAccessDate && !publicAccessDate) {
    return t('comingSoon', 'Coming Soon')
  }

  const isReleaseToday = guildEarlyAccessDate ? areDatesEqual(guildEarlyAccessDate, new Date()) : false

  if (isReleaseToday) {
    return handleReleaseToday({
      isReleaseToday,
      guildEarlyAccessDate,
      t,
      locale,
      fortnightAgo,
      isGuildMember,
    })
  }

  if (guildEarlyAccessDate && guildEarlyAccessDate.getTime() > now) {
    return Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(guildEarlyAccessDate)
  } else if (publicAccessDate && publicAccessDate.getTime() > now) {
    return Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(publicAccessDate)
  } else if (guildEarlyAccessDate && guildEarlyAccessDate.getTime() > fortnightAgo.getTime()) {
    return t('new', 'New')
  }

  return null
}

type HandleReleaseTodayParams = {
  isReleaseToday: boolean
  guildEarlyAccessDate: Date | null
  t: TranslateFunction
  locale: Locale
  fortnightAgo: Date
  isGuildMember: boolean
}

function handleReleaseToday({
  isReleaseToday,
  guildEarlyAccessDate,
  t,
  locale,
  fortnightAgo,
  isGuildMember,
}: HandleReleaseTodayParams) {
  if (isReleaseToday) {
    if (guildEarlyAccessDate && guildEarlyAccessDate.getTime() > Date.now()) {
      return t('todayAtTime', 'Today at {{time}}', {
        time: Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(guildEarlyAccessDate),
      })
    } else if (isGuildMember && guildEarlyAccessDate && guildEarlyAccessDate.getTime() > fortnightAgo.getTime()) {
      return t('new', 'New')
    }
    return null
  }
  return null
}
